import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';

import { useMounted } from 'src/hooks/use-mounted';
import { useBoolean } from 'src/hooks/use-boolean';
import { useGetFavicon } from 'src/hooks/use-favicon';
import { useResponsive } from 'src/hooks/use-responsive';

import { authApi } from 'src/api/auth';
import { formsApi } from 'src/api/forms';
import { announcementsApi } from 'src/api/announcement';
import { useDomainContext } from 'src/contexts/domain-context';

import { useSettingsContext } from 'src/components/settings';

import Main from './main';
import Header from './header';
import { FormModal } from './form-modal';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';
import { PreviewAnnouncementDialog } from './preview-announcement-dialog';

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
  
  const { domainData } = useDomainContext();
  const [open, setOpen] = useState(true);
  const [formList, setFormList] = useState([]);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);

  // Add new state for announcements
  const [announcements, setAnnouncements] = useState([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [showAnnouncement, setShowAnnouncement] = useState(false);

  const isMounted = useMounted();

  const settings = useSettingsContext();
  useGetFavicon();

  const lgUp = useResponsive('up', 'lg');

  const nav = useBoolean();

  const isHorizontal = settings.themeLayout === 'horizontal';

  const isMini = settings.themeLayout === 'mini';

  const renderHorizontal = <NavHorizontal />;

  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  const handleNextForm = () => {
    if(currentFormIndex + 1 <= formList?.length) setCurrentFormIndex(prev => prev + 1);
  }

  const handlePrevForm = () => {
    if(currentFormIndex > 0) setCurrentFormIndex(prev => prev - 1);
  }

  const handleSubmittedForm = (formId) => {
    const updatedForms = formList.filter(form => form.id !== formId);
    setFormList(updatedForms ?? [])
  }

  const handleClose = () => {
    if (formList.length === 0) {
      setOpen(false);
    }    
  }

  const markAnnouncementAsSeen = async (announcementId) => {
    try {
      await announcementsApi.updateAnnouncement({ setting_id: announcementId });
    } catch (error) {
      console.error("Error updating announcement status:", error);
    }
  };

  const handleCloseAnnouncement = async () => {
    // Mark current announcement as seen when closing
    await markAnnouncementAsSeen(announcements[currentAnnouncementIndex]?.setting_id);
    
    // If there are more announcements, show the next one
    if (currentAnnouncementIndex + 1 < announcements.length) {
      setCurrentAnnouncementIndex(prev => prev + 1);
    } else {
      // If this was the last announcement, close it and show forms if any
      setShowAnnouncement(false);
      setOpen(true);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await announcementsApi.getAnnouncements();
      if (response?.data?.announcements?.length > 0) {
        setAnnouncements(response?.data?.announcements);
        setShowAnnouncement(true);
        setOpen(false);
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  useEffect(() => {
    if (!isMounted()) return;
    const defaultSound = "/assets/sounds/connect.wav";
    const audio = new Audio(defaultSound);
    setTimeout(() => {
      audio.play();
    }, 2500);
  }, [isMounted]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const modalExists = document.querySelector('.form-modal') !== null;
      if (!modalExists) {
        if(open) {
          setOpen(false);
        } else {
          setTimeout(()=> setOpen(true), 500)
        }
      } 
    }, 1000); 
    return () => clearInterval(interval); 
  }, [open]);

  const initializeForms = async () => {
    const clientRes = await authApi.getUser();
    const { client } = clientRes.data;
    const { filled_forms, required_forms } = client;
    const unfilled_forms = required_forms.filter(form => !filled_forms.includes(form));
  
    try {
      const forms = await Promise.all(
        unfilled_forms.map(async (unfilled_form) => {
          const formRes = await formsApi.getForm(unfilled_form);
          return { ...formRes.data.form, settings: JSON.parse(formRes.data.form?.settings)};
        })
      );
      setFormList(forms ?? [])
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    initializeForms();
  }, [domainData]);

  if (isHorizontal) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main>{children}</Main>
        <PreviewAnnouncementDialog 
          open={showAnnouncement} 
          onClose={handleCloseAnnouncement}
          announcement={announcements[currentAnnouncementIndex]}
        />
        { 
          formList?.length > 0 && !showAnnouncement ?
            <FormModal 
              formId={formList[currentFormIndex]?.id} 
              settings={formList[currentFormIndex]?.settings ?? []} 
              open={open} 
              onClose={handleClose} 
              currentFormIndex={currentFormIndex}
              formListCount={formList?.length} 
              handleNextForm={handleNextForm} 
              handlePrevForm={handlePrevForm} 
              handleSubmittedForm={handleSubmittedForm}
              formName={formList[currentFormIndex]?.name}
            />
          : null
        }
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {renderNavVertical}

          <Main>{children}</Main>
        </Box>
        <PreviewAnnouncementDialog 
          open={showAnnouncement} 
          onClose={handleCloseAnnouncement}
          announcement={announcements[currentAnnouncementIndex]}
        />
        { 
          formList?.length > 0 && !showAnnouncement ?
            <FormModal 
              formId={formList[currentFormIndex]?.id} 
              settings={formList[currentFormIndex]?.settings ?? []} 
              open={open} 
              onClose={handleClose} 
              currentFormIndex={currentFormIndex}
              formListCount={formList?.length} 
              handleNextForm={handleNextForm} 
              handlePrevForm={handlePrevForm} 
              handleSubmittedForm={handleSubmittedForm}
              formName={formList[currentFormIndex]?.name}
            />
          : null
        }
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {renderNavVertical}

        <Main>{children}</Main>
      </Box>
        <PreviewAnnouncementDialog 
          open={showAnnouncement} 
          onClose={handleCloseAnnouncement}
          announcement={announcements[currentAnnouncementIndex]}
        />
        { 
          formList?.length > 0 && !showAnnouncement ?
            <FormModal 
              formId={formList[currentFormIndex]?.id} 
              settings={formList[currentFormIndex]?.settings ?? []} 
              open={open} 
              onClose={handleClose} 
              currentFormIndex={currentFormIndex}
              formListCount={formList?.length} 
              handleNextForm={handleNextForm} 
              handlePrevForm={handlePrevForm} 
              handleSubmittedForm={handleSubmittedForm}
              formName={formList[currentFormIndex]?.name}
            />
          : null
        }
    </>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
};
